import { createFileRoute } from '@tanstack/react-router'
import { Check, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/mais-opcoes/fechamento-de-mes')({
  component: Page,
})

/** Colunas da tabela de documentos do print (por ora sempre vazia). */
const COLUMNS = ['document', 'updatedAt', 'size', 'nature', 'name'] as const

/**
 * MOLDURA do fechamento de mês.
 *
 * Não há back-end de fechamento nem de documentos — então a tela replica o
 * layout do nibo sem fingir dado: a navegação de trimestre funciona (só no
 * estado local, movendo a janela de 3 meses), as tabelas nascem vazias com
 * "Nenhum documento no período", e "Fechar mês" fica DESABILITADO com o selo
 * "Em breve". O banner de topo (nav-config `coming-soon`) explica a causa.
 */
function Page() {
  const { t, i18n } = useTranslation(['moreOptions', 'common'])
  const lang = i18n.language
  // Offset em TRIMESTRES: cada passo desloca a janela de 3 meses.
  const [offset, setOffset] = useState(0)

  const now = new Date()
  const end = new Date(now.getFullYear(), now.getMonth() + offset * 3, 1)
  const start = new Date(end.getFullYear(), end.getMonth() - 2, 1)
  // 3 meses em ordem decrescente (mais recente primeiro), como no print.
  const months = [0, 1, 2].map((i) => new Date(end.getFullYear(), end.getMonth() - i, 1))

  const shortMonth = (d: Date) =>
    new Intl.DateTimeFormat(lang, { month: 'short' }).format(d).replace('.', '')
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  const longMonth = (d: Date) => cap(new Intl.DateTimeFormat(lang, { month: 'long' }).format(d))
  const monthSlug = (d: Date) => `${shortMonth(d).toLowerCase()}/${String(d.getFullYear())}`

  const rangeLabel = t('monthClose.range', {
    start: `${cap(shortMonth(start))}/${String(start.getFullYear())}`,
    end: `${cap(shortMonth(end))}/${String(end.getFullYear())}`,
  })

  // Agrupa por ano (a janela pode cruzar a virada de ano → dois títulos).
  // months já vem decrescente, então os anos distintos também saem decrescentes.
  const years = [...new Set(months.map((m) => m.getFullYear()))]
  const groups = years.map((year) => ({
    year,
    months: months.filter((m) => m.getFullYear() === year),
  }))

  const quarterNav = (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('monthClose.prevQuarter')}
        onClick={() => {
          setOffset((o) => o - 1)
        }}
      >
        <ChevronLeft aria-hidden />
      </Button>
      <span className="text-ink min-w-44 text-center text-sm font-semibold tabular-nums">
        {rangeLabel}
      </span>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('monthClose.nextQuarter')}
        onClick={() => {
          setOffset((o) => o + 1)
        }}
      >
        <ChevronRight aria-hidden />
      </Button>
    </div>
  )

  return (
    <>
      <PageHeader title={t('monthClose.title')} actions={quarterNav} />
      <div className="flex flex-col gap-6 p-6">
        {groups.map((g) => (
          <section key={g.year} className="flex flex-col gap-4">
            <h2 className="text-ink text-lg font-semibold tabular-nums">{g.year}</h2>
            {g.months.map((m) => (
              <MonthBlock
                key={`${String(g.year)}-${String(m.getMonth())}`}
                monthName={longMonth(m)}
                slug={monthSlug(m)}
              />
            ))}
          </section>
        ))}
      </div>
    </>
  )
}

function MonthBlock({ monthName, slug }: { readonly monthName: string; readonly slug: string }) {
  const { t } = useTranslation(['moreOptions', 'common'])

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-ink-muted text-xs font-semibold tracking-wide uppercase">
          {monthName}
        </h3>
        <div className="flex items-center gap-2">
          <Badge tone="soon">{t('common:comingSoon.badge')}</Badge>
          {/* Sem back-end de fechamento: o botão existe pela fidelidade ao print,
              porém desabilitado — nada a fechar de verdade ainda. */}
          <Button variant="primary" size="sm" disabled title={t('common:comingSoon.body')}>
            <Check aria-hidden />
            {t('monthClose.closeMonth', { month: slug })}
          </Button>
        </div>
      </div>

      <div className="border-line overflow-x-auto rounded-lg border">
        <table
          aria-label={t('monthClose.tableAria', { month: monthName })}
          className="w-full border-collapse text-sm"
        >
          <thead className="bg-surface-2">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c}
                  className="border-line text-ink-muted border-b px-3 py-2 text-left text-xs font-medium whitespace-nowrap"
                >
                  {t(`monthClose.columns.${c}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={COLUMNS.length}
                className="text-ink-subtle px-3 py-6 text-center text-sm"
              >
                {t('monthClose.empty')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
